import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Form,
  Grid,
  Icon,
  Popup,
  Image,
  Divider,
  Header,
} from "semantic-ui-react";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import { image } from "../../../../assets";
import { CustomTable } from "../../../Shared";
import { Student, User } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { initialValues, validationSchema } from "./ReentryStudentForm.form";
import { ENV, ubigeoData, withEmptyOption } from "../../../../utils";
import "./ReentryStudentForm.scss";

const userController = new User();
const studentController = new Student();

export function ReentryStudentForm(props) {
  const { academicCycleId } = useParams();
  const {
    onClose,
    onReload,
    gradesOptions,
    paymentCategoriesOptions,
    academicCycle,
    student,
  } = props;
  const { accessToken } = useAuth();

  const [reloadUsers, setReloadUsers] = useState(false);
  const [students, setStudents] = useState(null);
  const [studentActive, setStudentActive] = useState(null);
  const [filters, setFilters] = useState({ name: "", dni: "", email: "" });

  const [departamentOptions, setDepartamentOptions] = useState([]);
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);

  const formik = useFormik({
    initialValues: initialValues(student),
    validationSchema: validationSchema(student),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        formValue.academicCycleId = academicCycleId;
        formValue.syllabuId = academicCycle.syllabuId;
        console.log("formValue", formValue);
        await studentController.createReentryStudent(accessToken, formValue);
        onReload();
        onClose();
      } catch (error) {
        console.error(error);
      }
    },
  });

  const onReloadUsers = () => {
    setStudentActive(null);
    formik.setValues(initialValues(null));
    setReloadUsers((prevState) => !prevState);
  };

  useEffect(() => {
    const hasMinLength = Object.values(filters).some(
      (value) => value && value.length >= 3
    );

    if (hasMinLength) {
      (async () => {
        try {
          const response = await userController.getUsersToReentry(
            academicCycleId,
            filters
          );
          setStudents(response);
        } catch (error) {
          console.error(error);
        }
      })();
    } else {
      setStudents([]); // Opcional: limpia la lista si no hay suficientes caracteres
    }
  }, [reloadUsers]);

  useEffect(() => {
    (async () => {
      try {
        if (formik.values.province) {
          const province = formik.values.province;

          const dep = province.split("")[0] + "" + province.split("")[1];
          const pro = province.split("")[2] + "" + province.split("")[3];

          setDistrictOptions([]);
          const districts = ubigeoData.filter(
            (ubigeo) =>
              ubigeo.departamento === dep &&
              ubigeo.provincia === pro &&
              ubigeo.distrito !== "00"
          );
          const options = districts.map((ubigeo) => ({
            value:
              ubigeo.departamento +
              "" +
              ubigeo.provincia +
              "" +
              ubigeo.distrito,
            text: ubigeo.text,
          }));
          setDistrictOptions(options);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [formik.values.province]);

  useEffect(() => {
    (async () => {
      try {
        if (formik.values.departament) {
          const departament = formik.values.departament;

          const dep = departament.split("")[0] + "" + departament.split("")[1];

          setProvinceOptions([]);
          const provinces = ubigeoData.filter(
            (ubigeo) =>
              ubigeo.departamento === dep &&
              ubigeo.provincia !== "00" &&
              ubigeo.distrito === "00"
          );
          const options = provinces.map((ubigeo) => ({
            value:
              ubigeo.departamento +
              "" +
              ubigeo.provincia +
              "" +
              ubigeo.distrito,
            text: ubigeo.text,
          }));
          setProvinceOptions(options);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [formik.values.departament]);

  useEffect(() => {
    (async () => {
      try {
        const departaments = ubigeoData.filter(
          (ubigeo) => ubigeo.provincia === "00" && ubigeo.distrito === "00"
        );
        setDepartamentOptions([]);
        const options = departaments.map((ubigeo) => ({
          value:
            ubigeo.departamento + "" + ubigeo.provincia + "" + ubigeo.provincia,
          text: ubigeo.text,
        }));
        setDepartamentOptions(options);
      } catch (error) {
        console.error(error);
      }
    })();
  }, []);

  const setStudentDataToFormik = (row) => {
    setStudentActive(row);
    formik.setValues({
      ...formik.values,
      firstname: row.firstname,
      lastname: row.lastname,
      secondLastname: row.secondLastname,
      dni: row.dni,
      email: row.email,
      phone: row.phone,
      phone2: row.phone2,
      departament: row.departament,
      province: row.province,
      district: row.district,
      address: row.address,
      gradeId: row.gradeId,
      paymentCategoryId: row.paymentCategoryId,
      birthDate: row.birthDate,
      userId: row.id,
    });
    formik.setErrors({});
  };

  const tableConfig = {
    headers: [
      "Apellidos y Nombres",
      "DNI",
      "Correo electrónico",
      "Teléfono",
      "Acciones",
    ],
    fields: [
      {
        field: "lastname+ +secondLastname+ +firstname",
        dataType: "text",
      },
      {
        field: "dni",
        dataType: "text",
        align: "center",
      },
      {
        field: "email",
        dataType: "text",
      },
      {
        field: "phone",
        dataType: "text",
        align: "center",
      },
      {
        buttons: [
          {
            color: "blue",
            icon: "hand pointer outline",
            placeholder: "Seleccionar usuario",
            onClick: setStudentDataToFormik,
          },
        ],
      },
    ],
    columnWidths: ["35%", "15%", "35%", "15%"],
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    formik.setFieldValue("avatar", URL.createObjectURL(file));
    formik.setFieldValue("fileAvatar", file);
  });

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    onDrop,
  });

  const parseDate = (fecha) => {
    return fecha ? new Date(fecha).toISOString().split("T")[0] : "";
  };

  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return `${ENV.BASE_PATH}/${formik.values.avatar}`;
    }
    return image.noAvatar;
  };

  const handleChange = (e, { name, value }) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  return (
    <Form className="reentry-student-form" onSubmit={formik.handleSubmit}>
      <Grid columns={2} stackable>
        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h4" className="reentry-student-form__section-title">Buscar estudiante</Header>
            <div className="reentry-student-form__filters">
              <Form.Input
                name="name"
                label="Apellidos y nombres"
                placeholder="Buscar por apellidos y nombres"
                value={filters.name}
                onChange={handleChange}
              />
              <Form.Input
                name="dni"
                label="DNI"
                placeholder="Buscar por DNI"
                value={filters.dni}
                onChange={handleChange}
              />
              <Form.Input
                name="email"
                label="Correo electrónico"
                placeholder="Buscar por correo electrónico"
                value={filters.email}
                onChange={handleChange}
              />
              <Form.Button
                type="button"
                icon
                color="grey"
                className="reentry-student-form__filter-button"
                onClick={onReloadUsers}
              >
                <Icon name="filter" /> Filtrar
              </Form.Button>
            </div>
          </Grid.Column>
        </Grid.Row>

        {studentActive == null ? (
          <Grid.Row className="reentry-student-form__table">
            <Grid.Column>
              <CustomTable
                color="blue"
                tableConfig={tableConfig}
                data={students}
                onReload={onReload}
              />
            </Grid.Column>
          </Grid.Row>
        ) : (
          <Grid.Row className="reentry-student-form__data">
            <Grid.Column>
              <Header as="h4" className="reentry-student-form__section-title">Datos personales</Header>
              <Form.Input
                name="firstname"
                label="Nombre"
                placeholder="Ej. Juan Carlos"
                onChange={formik.handleChange}
                value={formik.values.firstname}
                error={formik.errors.firstname}
              />
              <Form.Input
                name="lastname"
                label="Apellido paterno"
                placeholder="Ej. Pérez"
                onChange={formik.handleChange}
                value={formik.values.lastname}
                error={formik.errors.lastname}
              />
              <Form.Input
                name="secondLastname"
                label="Apellido materno"
                placeholder="Ej. García"
                onChange={formik.handleChange}
                value={formik.values.secondLastname}
                error={formik.errors.secondLastname}
              />
              <Form.Input
                name="dni"
                label="DNI"
                placeholder="Ej. 12345678"
                onChange={formik.handleChange}
                value={formik.values.dni}
                error={formik.errors.dni}
              />
              <Popup
                content="Fecha de nacimiento"
                trigger={
                  <Form.Input
                    name="birthDate"
                    label="Fecha de nacimiento"
                    type="date"
                    placeholder="Fecha de nacimiento"
                    onChange={formik.handleChange}
                    value={parseDate(formik.values.birthDate)}
                    error={formik.errors.birthDate}
                  />
                }
              />
              <Form.Input
                name="email"
                label="Correo electrónico"
                placeholder="Ej. juan.perez@iepsanisidro.edu.pe"
                onChange={formik.handleChange}
                value={formik.values.email}
                error={formik.errors.email}
              />
            </Grid.Column>
            <Grid.Column>
              {typeof student === "object" && student !== null ? (
                <div className="student-form__avatar" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <Image avatar size="small" src={getAvatar()} />
                </div>
              ) : (
                student
              )}
              <Header as="h4" className="reentry-student-form__section-title">Ubicación y contacto</Header>
              <Form.Select
                className="edunova-select"
                name="departament"
                label="Departamento"
                placeholder="Selecciona un departamento"
                clearable
                options={withEmptyOption(departamentOptions, "Selecciona un departamento")}
                onChange={(e, { value }) => {
                  formik.setFieldValue("departament", value);
                  formik.setFieldValue("province", null);
                  formik.setFieldValue("district", null);
                }}
                value={formik.values.departament}
                error={formik.errors.departament}
              />
              <Form.Select
                className="edunova-select"
                name="province"
                label="Provincia"
                placeholder="Selecciona una provincia"
                clearable
                options={withEmptyOption(provinceOptions, "Selecciona una provincia")}
                onChange={(e, { value }) =>
                  formik.setFieldValue("province", value)
                }
                value={formik.values.province}
                error={formik.errors.province}
              />
              <Form.Select
                className="edunova-select"
                name="district"
                label="Distrito"
                placeholder="Selecciona un distrito"
                clearable
                options={withEmptyOption(districtOptions, "Selecciona un distrito")}
                onChange={(e, { value }) =>
                  formik.setFieldValue("district", value)
                }
                value={formik.values.district}
                error={formik.errors.district}
              />
              <Form.Input
                name="address"
                label="Domicilio"
                placeholder="Ej. Av. Los Álamos 123"
                onChange={formik.handleChange}
                value={formik.values.address}
                error={formik.errors.address}
              />
              <Form.Input
                name="phone"
                label="Teléfono"
                placeholder="Ej. 987654321"
                onChange={formik.handleChange}
                value={formik.values.phone}
                error={formik.errors.phone}
              />
              <Form.Input
                name="phone2"
                label="Teléfono adicional"
                placeholder="Ej. 912345678 (opcional)"
                onChange={formik.handleChange}
                value={formik.values.phone2}
                error={formik.errors.phone2}
              />
            </Grid.Column>
          </Grid.Row>
        )}

        <Grid.Row
          className={`reentry-student-form__hidden ${
            studentActive != null ? "active" : ""
          }`}
        >
          <Form.Input
            type="hidden"
            name="userId"
            value={formik.values.userId}
            error={formik.errors.userId}
          />
        </Grid.Row>

        <Divider className="reentry-student-form__separator" />
        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h4" className="reentry-student-form__section-title">Matrícula</Header>
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="gradeId"
              label="Grado"
              placeholder="Selecciona un grado"
              clearable
              options={withEmptyOption(gradesOptions, "Selecciona un grado")}
              onChange={(e, { value }) => {
                formik.setFieldValue("gradeId", value);
              }}
              value={formik.values.gradeId}
              error={formik.errors.gradeId}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="paymentCategoryId"
              label="Categoría de pago"
              placeholder="Selecciona una categoría de pago"
              clearable
              options={withEmptyOption(paymentCategoriesOptions, "Selecciona una categoría de pago")}
              onChange={(e, { value }) =>
                formik.setFieldValue("paymentCategoryId", value)
              }
              value={formik.values.paymentCategoryId}
              error={formik.errors.paymentCategoryId}
            />
          </Grid.Column>
        </Grid.Row>

        <Divider className="reentry-student-form__separator" />

        <Grid.Row>
          <Grid.Column width={16}>
            <Header as="h4" className="reentry-student-form__section-title">Datos del apoderado/tutor</Header>
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Input
              name="dni_tutor"
              label="DNI del tutor"
              placeholder="Ej. 12345678"
              onChange={formik.handleChange}
              value={formik.values.dni_tutor}
              error={formik.errors.dni_tutor}
            />
          </Grid.Column>
          <Grid.Column width={8}>
            <Form.Select
              className="edunova-select"
              name="familyRelationship"
              label="Parentesco"
              placeholder="Selecciona un parentesco"
              clearable
              options={withEmptyOption(
                [
                  { value: 1, text: "Padre" },
                  { value: 2, text: "Madre" },
                  { value: 3, text: "Hermano(a)" },
                  { value: 4, text: "Tío(a)" },
                  { value: 5, text: "Abuelo(a)" },
                  { value: 6, text: "Padrino(a)" },
                  { value: 7, text: "Otro" },
                ],
                "Selecciona un parentesco"
              )}
              onChange={(e, { value }) =>
                formik.setFieldValue("familyRelationship", value)
              }
              value={formik.values.familyRelationship}
              error={formik.errors.familyRelationship}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <Form.Input
              name="name_tutor"
              label="Nombre del tutor"
              placeholder="Ej. María López García"
              onChange={formik.handleChange}
              value={formik.values.name_tutor}
              error={formik.errors.name_tutor}
            />
          </Grid.Column>
        </Grid.Row>

        <Form.Button
          className="reentry-student-form__button"
          type="submit"
          primary
          fluid
          loading={formik.isSubmitting}
        >
          {student ? "Actualizar estudiante" : "Matricular estudiante"}
        </Form.Button>
      </Grid>
    </Form>
  );
}
